/**
 * SweetAlert 1.x API backed by CBAR dialogs.
 *
 * Vendor SweetAlert is no longer shipped under public/vendor/; call sites still
 * use global `swal(...)`. This shim keeps those working without the missing asset.
 */
import {
    asCbarDialog,
    closeCbarDialog,
    openCbarDialog,
    whenCbarDialogReady,
    type CbarDialogElement,
} from './cbar-dialog-helpers';
import {cbarAlert, cbarConfirm} from './cbar-message-dialog';

export type SwalOptions = {
    title?: string;
    text?: string;
    type?: string;
    showCancelButton?: boolean;
    confirmButtonColor?: string;
    confirmButtonText?: string;
    cancelButtonText?: string;
    closeOnConfirm?: boolean;
    showLoaderOnConfirm?: boolean;
    closeOnCancel?: boolean;
    showConfirmButton?: boolean;
};

type SwalCallback = (isConfirm: boolean) => void;

type SwalFn = {
    (options: SwalOptions, callback?: SwalCallback): void;
    (title: string, text?: string, type?: string, callback?: SwalCallback): void;
    close: () => void;
};

const BUSY_DIALOG_ID = 'cbar-swal-busy-dialog';

function escapeHtml(text: string): string {
    return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function ensureBusyDialog(): CbarDialogElement {
    let dialog = asCbarDialog(document.getElementById(BUSY_DIALOG_ID));
    if (dialog) {
        if (dialog.parentElement !== document.body) {
            document.body.appendChild(dialog);
        }
        return dialog;
    }

    dialog = document.createElement('cbar-dialog') as CbarDialogElement;
    dialog.id = BUSY_DIALOG_ID;
    dialog.className = 'cbar-message-dialog cbar-swal-busy-dialog';
    dialog.setAttribute('aria-busy', 'true');
    dialog.innerHTML = `
        <div slot="headline" class="cbar-message-dialog__headline"></div>
        <div slot="content" class="cbar-message-dialog__content">
            <p class="cbar-message-dialog__message"></p>
        </div>
    `;
    document.body.appendChild(dialog);
    dialog.open = false;
    return dialog;
}

async function showBusy(title: string, text: string): Promise<void> {
    await whenCbarDialogReady();
    const dialog = ensureBusyDialog();
    const headline = dialog.querySelector('.cbar-message-dialog__headline');
    const message = dialog.querySelector('.cbar-message-dialog__message');
    if (headline) {
        headline.textContent = title || 'Please wait';
    }
    if (message) {
        message.innerHTML = escapeHtml(text || 'Please wait…');
    }
    openCbarDialog(dialog);
}

function closeBusy(): void {
    const dialog = asCbarDialog(document.getElementById(BUSY_DIALOG_ID));
    closeCbarDialog(dialog);
}

function titleForType(type: string | undefined, fallback: string): string {
    if (fallback) {
        return fallback;
    }
    switch (type) {
        case 'error':
            return 'Error';
        case 'warning':
            return 'Warning';
        case 'success':
            return 'Success';
        case 'info':
            return 'Notice';
        default:
            return 'Notice';
    }
}

function isBusyOptions(options: SwalOptions): boolean {
    if (options.showCancelButton) {
        return false;
    }
    if (options.showConfirmButton === false) {
        return true;
    }
    const title = String(options.title ?? '');
    const text = String(options.text ?? '');
    return /please wait/i.test(`${title} ${text}`);
}

async function runAlert(title: string, text: string, type?: string): Promise<void> {
    closeBusy();
    const hasBody = text.trim() !== '';
    await cbarAlert(hasBody ? text : title, {
        title: hasBody ? titleForType(type, title) : titleForType(type, 'Notice'),
        confirmLabel: 'OK',
    });
}

async function runConfirm(options: SwalOptions, callback?: SwalCallback): Promise<void> {
    closeBusy();
    const ok = await cbarConfirm(options.text ?? '', {
        title: options.title ?? 'Confirm',
        confirmLabel: options.confirmButtonText ?? 'Confirm',
        cancelLabel: options.cancelButtonText ?? 'Cancel',
    });
    callback?.(ok);
}

function swalImpl(optionsOrTitle: SwalOptions | string, textOrCallback?: string | SwalCallback, type?: string, callback?: SwalCallback): void {
    if (typeof optionsOrTitle === 'string') {
        if (typeof textOrCallback === 'function') {
            void runAlert(optionsOrTitle, '', undefined).then(() => {
                textOrCallback(true);
            });
            return;
        }
        const text = typeof textOrCallback === 'string' ? textOrCallback : '';
        if (isBusyOptions({title: optionsOrTitle, text, type})) {
            void showBusy(optionsOrTitle, text);
            return;
        }
        void runAlert(optionsOrTitle, text, type).then(() => {
            callback?.(true);
        });
        return;
    }

    const options = optionsOrTitle;
    const cb = typeof textOrCallback === 'function' ? textOrCallback : callback;

    if (options.showCancelButton || typeof cb === 'function') {
        if (options.showCancelButton) {
            void runConfirm(options, cb);
            return;
        }
        // Callback without cancel: treat as alert then invoke callback(true).
        void runAlert(options.title ?? '', options.text ?? '', options.type).then(() => {
            cb?.(true);
        });
        return;
    }

    if (isBusyOptions(options)) {
        void showBusy(options.title ?? '', options.text ?? '');
        return;
    }

    void runAlert(options.title ?? '', options.text ?? '', options.type);
}

export const swal: SwalFn = Object.assign(swalImpl, {
    close: closeBusy,
}) as SwalFn;

export function installSwalCompat(): void {
    if (typeof window === 'undefined') {
        return;
    }
    (window as Window & {swal: SwalFn}).swal = swal;
}

declare global {
    interface Window {
        swal: SwalFn;
    }
}
